<template>
  <Card tag="form" variant="widget" class="project-mutate-presentation" @submit.prevent="$emit('submit')">
    <template #header>
      <div class="card-v2__title">{{ $t('frontPage') }}</div>
      <FieldBuilder
        v-model="value.themeLayout"
        :item="item"
        :customFieldSchemas="customFieldSchemas"
        :placement="placement"
        :build="buildLayout"
      />
    </template>
    <template #default>
      <div class="project-mutate-presentation__input">
        <FieldRenderer
          class="project-mutate-presentation__field-render-item-default"
          v-for="item in fields.default"
          :item="item"
          :value="value"
        />

        <CustomFields
          v-model="value.customFields"
          :schema="cfSchemas"
          :data="data"
          :validator="validator"
          validator-prefix="customFields"
          :placement="placement"
          :disabledFields="$path('metadata.disabledFields.project', item)"
        >
          <template v-for="item in fields.slots" v-slot:[item.slotKey]>
            <FieldRenderer :item="item" :value="value" />
          </template>
        </CustomFields>
      </div>
    </template>
  </Card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { i18n } from '@/i18n'
import Card from '@/components/Card-v2'

import CustomFieldSchemas from '@/components/Project/CustomFieldSchemas'
import { AlertComponent } from 'vue-elder-alert'
import FieldRenderer from '@/components/Project/StepsFields/FieldRenderer.vue'
import CustomFields from '@kvass/custom-fields'
import FieldBuilder from '@/components/FieldBuilder'
import { getCustomFieldsFromLayout } from '@/components/FieldBuilder/utils'

const meta = {
  order: 3,
  id: 'presentation',
  label: i18n.tc('frontPage'),
  icon: ['fal', 'image'],
}

export { meta }
export default {
  props: {
    value: Object,
    data: Object,
    labels: Object,
    validator: Object,
    fields: {
      type: Object,
      default: () => ({}),
    },
    customFieldSchemas: { type: Array, default: () => [] },
  },
  data() {
    return {
      placement: ['presentation', 'project:presentation', undefined, null],
    }
  },

  computed: {
    ...mapGetters('Project', ['getDisabledField']),
    ...mapState('Project', {
      item(state) {
        return state.item && Object.keys(state.item).length ? state.item : this.data
      },
      states: 'states',
    }),
    buildLayout() {
      return this.$path('theme', this.value) === 'plain-project'
    },

    cfSchemas() {
      if (!this.value) return
      if (!this.buildLayout) return this.customFieldSchemas
      return getCustomFieldsFromLayout(this.value.themeLayout, this.customFieldSchemas, this.placement)
    },

    isUpcoming() {
      let status = this.$path('item.metadata.residentialProperties.status') || []
      return !this.states.fetchOne && (!status.length || status.every(s => ['draft', 'upcoming'].includes(s)))
    },
  },

  components: {
    Card,
    CustomFields,
    CustomFieldSchemas,
    AlertComponent,
    FieldRenderer,
    FieldBuilder,
  },
}
</script>

<style lang="scss">
.project-mutate-presentation {
  --columns: 12;

  .elder-loader {
    flex-grow: 0;
  }

  &__header {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: start;
    justify-content: space-between;
    padding: var(--card-spacing);
    padding-bottom: 0;

    p {
      margin: 0;
    }
  }

  & > .card-v2 {
    &__header {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }
  & > .card__content {
    padding: 0;
  }

  &__input {
    display: grid;
    grid-template-columns: repeat(var(--columns), 1fr);
    grid-gap: $form-gap;

    padding: var(--card-spacing);
  }

  &__field-render-item-default {
    &[data-field='description'] {
      grid-row-end: span 3;
      grid-column-end: span calc(var(--columns) / 4 * 3);
      display: flex;
      flex-grow: 1;
    }
  }

  [data-field='media.logo'],
  [data-field='media.logoInverted'],
  [data-field='media.favicon'] {
    grid-column-end: span calc(var(--columns) / 4);
    height: 100%;

    .kvass-media-droparea {
      min-height: 120px;
    }
  }

  [data-field='media.gallery'],
  [data-field='media.cover'],
  [data-field='media.brochure'],
  [data-field='media.attachments'] {
    grid-column-end: span calc(var(--columns) / 2);

    .kvass-media-droparea,
    .elder-image__droparea {
      height: 320px;
      flex-grow: 0;
    }
  }

  .custom-fields {
    grid-column-end: span var(--columns);
  }
}

.project-mutate-presentation-upcoming-alert {
  @include respond-above('phone') {
    position: fixed;
    left: 0;
    bottom: 0;
    z-index: get-layer('modal') - 1;
    max-width: 35ch;
    border-top-right-radius: var(--border-radius);
    overflow: hidden;

    & > .alert {
      box-shadow: 0 15px 15px -5px rgba(black, 0.2);
      border-radius: 0;
    }
  }
}
</style>
